import React, { useState, useCallback } from 'react';
import {  Link, useNavigate } from 'react-router-dom';
import { useSidebar } from '../sidebar';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getSidebarContent, getSidebarFooter } from '@/lib/pages';
import { useFolderStructureVisibility, FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';
import CompanySwitcher from '@/components/ui/dropdowns/CompanySwitcher';
import RoleSwitcher from '@/components/ui/dropdowns/RoleSwitcher';
import ThemeSwitcher from '@/components/ui/dropdowns/ThemeSwitcher';
import { useTabContext } from '@/contexts/TabContext';
import ContextMenu, { ContextMenuItem } from '@/components/ui/ContextMenu';
import { useDevRoleOverride } from '@/lib/utils/devRoleOverride';
import { IconProvider } from '@/components/ui';
import * as Sentry from '@sentry/react';

// Mapping of sidebar item paths to folder structure types
const PATH_TO_FOLDER_TYPE: Record<string, FolderStructureType> = {
  /* '/library': 'library',
  '/documents': 'documents',
  '/templates': 'blueprints',
  '/dialogs': 'dialogs',
  '/variables': 'variables',
  '/logic': 'flows', */
};

// AppSidebar Content component - custom implementation for unified layout
const AppSidebarContent: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const { selectedGame, selectedCompany, currentUser } = useSimplifiedUserData();
  const { open: sidebarOpen } = useSidebar();
  const { toggleFolderType, isFolderTypeActive, setSelectedFolderForType } = useFolderStructureVisibility();
  const { tabs } = useTabContext();
  
  // Subscribe to dev role override changes to trigger re-render when role changes
  const overrideRoleId = useDevRoleOverride((state) => state.overrideRoleId);
  
  // Get user's sidebar visibility preferences
  const sidebarVisibility = currentUser?.settings?.sidebarVisibility;

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    path: string;
    label: string;
    icon: any;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    path: '',
    label: '',
    icon: null,
  });

  // Check if any tab matches the given path (for active state highlighting)
  const isActive = (path: string | undefined) => {
    if (!path) return false;
    
    // Check if any active tab matches this path
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return false;
    
    // Check if the active tab's path starts with this sidebar item's path
    return activeTab.path.startsWith(path);
  };
  const isOwner = true; // TODO: Implement proper role check
  
  // Get sidebar content with visibility preferences and role override awareness
  const sidebarContent = getSidebarContent(
    selectedGame, 
    selectedCompany, 
    isOwner, 
    user?.email,
    sidebarVisibility
  );
  
  const sidebarFooter = getSidebarFooter(
    selectedGame, 
    selectedCompany, 
    isOwner, 
    user?.email,
    sidebarVisibility
  );

  // Handle sidebar item click - just navigate, useAutoTabs will handle tab creation
  const handleItemClick = (e: React.MouseEvent, path: string, label: string, icon: any) => {
    const folderType = PATH_TO_FOLDER_TYPE[path];
    if (folderType) {
      // This is a folder structure page - ensure folder type is active without hiding others
      // Only toggle if not already active (keeps other folders open)
      if (!isFolderTypeActive(folderType)) {
        toggleFolderType(folderType);
      }
      
      // Reset the selected folder to root for this folder type
      setSelectedFolderForType(folderType, null);
    }
    // Let the Link component handle navigation - useAutoTabs will create a new tab
  };

  // Handle right-click to show context menu
  const handleContextMenu = (e: React.MouseEvent, path: string, label: string, icon: any) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      path,
      label,
      icon,
    });
  };

  // Handle opening in platform tab
  const handleOpenInTab = () => {
    const folderType = PATH_TO_FOLDER_TYPE[contextMenu.path];
    if (folderType) {
      // For folder structure pages, ensure folder type is active without hiding others
      if (!isFolderTypeActive(folderType)) {
        toggleFolderType(folderType);
      }
      
      // Reset the selected folder to root for this folder type
      setSelectedFolderForType(folderType, null);
    }
    
    // Navigate to the path - useAutoTabs will handle tab creation
    navigate(contextMenu.path);
  };

  // Handle opening in browser tab
  const handleOpenInBrowser = () => {
    window.open(contextMenu.path, '_blank');
  };

  // Context menu items
  const contextMenuItems: ContextMenuItem[] = [
    {
      id: 'open-in-tab',
      label: 'Open in Tab',
      icon: 'TabIcon',
      onClick: handleOpenInTab,
    },
    {
      id: 'open-in-browser',
      label: 'Open in Browser',
      icon: 'ExternalLink',
      onClick: handleOpenInBrowser,
      separator: true,
    },
  ];

  // Open Sentry feedback dialog
  const handleReportBug = useCallback(async () => {
    const feedback = Sentry.getFeedback();
    if (feedback) {
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
    }
  }, []);

  // Handle logout action
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/login');
    }
  };

  return (
    <div 
      className={cn(
        "flex h-full flex-col bg-sidebar text-sidebar-foreground transition-all duration-200 border-r border-sidebar-border",
        sidebarOpen ? "w-[16rem]" : "w-[3rem]"
      )}
      data-state={sidebarOpen ? "expanded" : "collapsed"}
    >     

      {/* Sidebar Content */}
      <div className="flex-1 overflow-y-auto px-2 pt-4">
        {sidebarContent.map((group, index) => (
          <div key={group.id} className="py-2">
            {sidebarOpen && (
              <h4 className="mb-2 px-2 text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
                {group.label}
              </h4>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={(e) => handleItemClick(e, item.path, item.label, item.icon)}
                  onContextMenu={(e) => handleContextMenu(e, item.path, item.label, item.icon)}
                  className={cn(
                    "flex items-center gap-2 rounded-md text-sm transition-colors",
                    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isActive(item.path) && "bg-sidebar-accent text-sidebar-accent-foreground",
                    item.disabled && "opacity-50 pointer-events-none",
                    sidebarOpen ? "px-2 py-2" : "justify-center w-8 h-8 p-0"
                  )}
                  title={!sidebarOpen ? item.label : undefined}
                >
                  <IconProvider iconstring={item.icon} size={16} />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              ))}
            </div>
            {/* Add separator between groups when collapsed */}
            {!sidebarOpen && index < sidebarContent.length - 1 && (
              <div className="mt-2 mx-auto w-6 h-px bg-sidebar-border/40" />
            )}
          </div>
        ))}
      </div>
      
      {/* Sidebar Footer */}
      <div className="mt-auto px-2 pb-4">
        {/* Add separator before footer when collapsed */}
        {!sidebarOpen && sidebarFooter.length > 0 && (
          <div className="mb-2 mx-auto w-6 h-px bg-sidebar-border/40" />
        )}
        
        {/* Theme Switcher - only in development mode for testing themes */}
        <ThemeSwitcher collapsed={!sidebarOpen} />
        
        {/* Role Switcher - only in development mode for testing permissions */}
        <RoleSwitcher collapsed={!sidebarOpen} />
        
        {/* Company Switcher - only show if user has multiple companies */}
        <CompanySwitcher collapsed={!sidebarOpen} />
        
        {/* Report a Bug button */}
        <div className="py-2">
          <div className="space-y-1">
            <button
              onClick={handleReportBug}
              className={cn(
                "flex items-center gap-2 rounded-sm text-sm transition-colors text-start",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                sidebarOpen ? "px-2 py-2 w-full" : "justify-center w-8 h-8 p-0"
              )}
              title={!sidebarOpen ? "Report a Bug" : undefined}
            >
              <IconProvider iconstring="fa-bug" style="light" size={16} color="red" />
              {sidebarOpen && <span>Report a Bug</span>}
            </button>
          </div>
        </div>

        {sidebarFooter.map((group) => (
          <div key={group.id} className="py-2">
            {sidebarOpen && (
              <h4 className="mb-2 px-2 text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
                {group.label}
              </h4>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                item.id === "logout" ? (
                  <button
                    key={item.id}
                    onClick={handleLogout}
                    className={cn(
                      "flex items-center gap-2 rounded-sm text-sm transition-colors text-start",
                      "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      item.disabled && "opacity-50 pointer-events-none",
                      sidebarOpen ? "px-2 py-2 w-full" : "justify-center w-8 h-8 p-0"
                    )}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <IconProvider iconstring={item.icon} size={16} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </button>
                ) : (
                  <Link
                    key={item.id}
                    to={item.path || '#'}
                    onClick={(e) => item.path && handleItemClick(e, item.path, item.label, item.icon)}
                    onContextMenu={(e) => item.path && handleContextMenu(e, item.path, item.label, item.icon)}
                    className={cn(
                      "flex items-center gap-2 rounded-sm text-sm transition-colors",
                      "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      isActive(item.path) && "bg-sidebar-accent text-sidebar-accent-foreground",
                      item.disabled && "opacity-50 pointer-events-none",
                      sidebarOpen ? "px-2 py-2" : "justify-center w-8 h-8 p-0"
                    )}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <IconProvider iconstring={item.icon} size={16} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                )
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        items={contextMenuItems}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
      />
    </div>
  );
};

export default AppSidebarContent;
